import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';

const { UUID } = sdkTypes;

// ================ Action types ================

export const SHOW_LISTING_REQUEST = 'app/SubmitOfferPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/SubmitOfferPage/SHOW_LISTING_ERROR';

// ================ Reducer ================

const initialState = {
  id: null,
  showListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: id,
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: storableError(e),
});

// ================ Thunks ================

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.facebook',
      'variants.twitter',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const entities = denormalisedResponseEntities(data);
      dispatch(addMarketplaceEntities(entities));
      return { data };
    })
    .catch(e => {
      // If fetching as regular listing fails, try as own listing
      if (!isOwn && e.status === 404) {
        // Try as own listing
        return dispatch(showListing(listingId, config, true));
      }
      dispatch(showListingError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;

  // Try to fetch as regular listing first, then as own listing if it fails
  return dispatch(showListing(listingId, config, false)).catch(e => {
    // If it's a 404 and user is logged in, try as own listing
    if (e.status === 404 && currentUser) {
      return dispatch(showListing(listingId, config, true));
    }
    throw e;
  });
};
